import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Sun, Eye, EyeOff, AlertCircle, Beaker } from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'
import { isDemoMode } from '@/lib/supabase'
import { useNavigate } from 'react-router-dom'

// =============================================================================
// Form schema
// =============================================================================

const schema = z.object({
  email:    z.string().email('Email no válido'),
  password: z.string().min(6, 'Mínimo 6 caracteres'),
})

type FormData = z.infer<typeof schema>

// =============================================================================
// LoginPage
// =============================================================================

export default function LoginPage() {
  const { login, isLoading, error }  = useAuth()
  const navigate                     = useNavigate()
  const [showPass, setShowPass]      = useState(false)

  const { register, handleSubmit, formState: { errors } } = useForm<FormData>({
    resolver: zodResolver(schema),
  })

  const onSubmit = async (data: FormData) => {
    const ok = await login(data.email, data.password)
    if (ok) navigate('/', { replace: true })
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-brand-900 flex items-center justify-center p-4">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-96 h-96 rounded-full bg-brand-600/10 blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-96 h-96 rounded-full bg-yellow-400/5 blur-3xl" />
      </div>

      <div className="relative w-full max-w-sm">
        {/* Card */}
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-slate-800 to-slate-900 px-8 py-8 text-center">
            <div className="flex items-center justify-center w-14 h-14 rounded-2xl bg-yellow-400 mx-auto mb-4">
              <Sun className="w-8 h-8 text-yellow-900" />
            </div>
            <h1 className="text-xl font-bold text-white">PSFV Navabuena</h1>
            <p className="text-slate-400 text-sm mt-1">EPC Construction Tracker</p>
            <div className="mt-3 flex items-center justify-center gap-2">
              <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
              <span className="text-xs text-slate-400">Sistema en producción</span>
            </div>
          </div>

          {/* Demo mode banner */}
          {isDemoMode && (
            <div className="mx-4 mt-4 flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5">
              <Beaker className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
              <div>
                <p className="text-xs font-semibold text-amber-800">Modo Demo</p>
                <p className="text-xs text-amber-700 mt-0.5">Usa cualquier email/contraseña. Añade "admin" o "subcontrat" al email para cambiar de rol.</p>
              </div>
            </div>
          )}

          {/* Form */}
          <div className="px-8 py-7">
            <h2 className="text-lg font-semibold text-gray-900 mb-1">Iniciar sesión</h2>
            <p className="text-sm text-gray-500 mb-6">Accede con tus credenciales de usuario</p>

            <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Correo electrónico
                </label>
                <input
                  {...register('email')}
                  type="email"
                  autoComplete="email"
                  placeholder="usuario@empresa.com"
                  className={`input-base text-sm ${errors.email ? 'border-red-300 focus:ring-red-500' : ''}`}
                />
                {errors.email && (
                  <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {errors.email.message}
                  </p>
                )}
              </div>

              {/* Password */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Contraseña
                </label>
                <div className="relative">
                  <input
                    {...register('password')}
                    type={showPass ? 'text' : 'password'}
                    autoComplete="current-password"
                    placeholder="••••••••"
                    className={`input-base text-sm pr-10 ${errors.password ? 'border-red-300 focus:ring-red-500' : ''}`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPass(!showPass)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {errors.password && (
                  <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {errors.password.message}
                  </p>
                )}
              </div>

              {/* Server error */}
              {error && (
                <div className="flex items-center gap-2 text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2.5">
                  <AlertCircle className="w-4 h-4 shrink-0" />
                  <p className="text-sm">{error}</p>
                </div>
              )}

              {/* Submit */}
              <button
                type="submit"
                disabled={isLoading}
                className="btn-primary w-full justify-center py-2.5 text-sm mt-2"
              >
                {isLoading ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  'Acceder'
                )}
              </button>
            </form>
          </div>
        </div>

        {/* Footer note */}
        <p className="text-center text-xs text-slate-500 mt-6">
          Acceso restringido a personal autorizado COBRA / Navabuena Solar
        </p>
        <p className="text-center text-xs text-slate-600 mt-1">
          © {new Date().getFullYear()} PSFV Navabuena — v1.0
        </p>
      </div>
    </div>
  )
}
